import { useQuery } from '@apollo/client';
import { Callout, DirectionalHint, IconButton, Stack } from '@fluentui/react';
import { HistoryActionMenuView } from 'common/components/History';
import { PivotDisplay } from 'common/components/PivotDisplay';
import { ListInvoiceNotesVariables } from 'common/graphql/__generated__/ListInvoiceNotes';
import React, { useCallback, useRef, useState } from 'react';

import { FlowApproval } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { useStyles } from './index.styles';
import {
  ListApprovalNotes,
  ListApprovalNotesVariables,
} from 'settings/account/approvalSetup/view/ActionMenu/ApprovalSetupNotesSection/__generated__/ListApprovalNotes';
import { ApprovalSetupNotesSection } from 'settings/account/approvalSetup/view/ActionMenu/ApprovalSetupNotesSection';

import ENTITY_NOTES_LIST from '../../../../approvalSetup/view/ActionMenu/ApprovalSetupNotesSection/ListApprovalNotes.graphql';

interface ApprovalDetailsProps {
  approvalDetails: FlowApproval | undefined;
}

export const ActionsMenu: React.FC<ApprovalDetailsProps> = ({
  approvalDetails,
}) => {
  const styles = useStyles();

  const [expandNotes, setExpandNotes] = useState(false);
  const [expandHistory, setExpandHistory] = useState(false);

  const notesRef = useRef<HTMLDivElement | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);

  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListApprovalNotes,
    ListApprovalNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id: approvalDetails?.approval?.id!,
    },
    skip: !approvalDetails?.approval?.id,
  });

  const getEntityNotes = useCallback(() => {
    const variables: ListInvoiceNotesVariables = {
      id: approvalDetails?.approval?.id!,
    };
    entityNotesListRefetch(variables);
  }, [approvalDetails?.approval?.id, entityNotesListRefetch]);

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
      <div
        ref={notesRef}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <PivotDisplay
          name="Notes"
          iconName="Page"
          count={entityNotesData?.approval?.notesByEntityId.nodes.length}
          onPivotClick={() => {
            setExpandNotes((prev) => !prev);
            setExpandHistory(false);
          }}
        />
        <IconButton
          onClick={() => {
            setExpandNotes((prev) => !prev);
            setExpandHistory(false);
          }}
          iconProps={{
            iconName: expandNotes ? 'ChevronUp' : 'ChevronDown',
          }}
          className={styles.iconButtonColor}
          ariaLabel="Toggle Notes"
        />
      </div>

      {/* History + Arrow */}
      <div ref={historyRef} style={{ display: 'flex', alignItems: 'center' }}>
        <PivotDisplay
          name="History"
          iconName="History"
          count={
            approvalDetails?.approval?.approvalSetupHistoriesByEntityId.nodes
              .length
          }
          onPivotClick={() => {
            setExpandHistory((prev) => !prev);
            setExpandNotes(false);
          }}
        />
        <IconButton
          onClick={() => {
            setExpandHistory((prev) => !prev);
            setExpandNotes(false);
          }}
          iconProps={{
            iconName: expandHistory ? 'ChevronUp' : 'ChevronDown',
          }}
          className={styles.iconButtonColor}
          ariaLabel="Toggle History"
        />
      </div>

      {/* Notes Callout */}
      {expandNotes && (
        <Callout
          target={notesRef.current}
          onDismiss={() => setExpandNotes(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible
          gapSpace={10}
          setInitialFocus
        >
          <ApprovalSetupNotesSection
            data={approvalDetails?.approval?.notesByEntityId.nodes!}
            listRefreshRequest={getEntityNotes}
            id={approvalDetails?.approval?.id!}
          />
        </Callout>
      )}

      {/* History Callout */}
      {expandHistory && (
        <Callout
          target={historyRef.current}
          onDismiss={() => setExpandHistory(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible
          gapSpace={10}
          setInitialFocus
        >
          <HistoryActionMenuView
            moduleName="Approval Setup"
            isApprovalFlow
            numberOfItems={4}
            historyData={
              approvalDetails?.approval?.approvalSetupHistoriesByEntityId
                .nodes || []
            }
          />
        </Callout>
      )}
    </Stack>
  );
};
